import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useFetcher } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import {
	CREATE_PRODUCT_REVIEW_FORM,
	CREATE_PRODUCT_REVIEW_INPUTS,
	createProductReviewSchema,
} from '#app/components/forms/create-product-review-form/create-product-review-form.tsx'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { PRODUCTS_SLUG_REVIEW_CREATE_I18next } from '#app/routes/($lang).products.$slug.review.create/route-config'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'

type UseCreateProductReviewProps = {
	product: GetProduct
	loggedIn: boolean
}

function useCreateProductReviewForm({
	product,
	loggedIn,
}: UseCreateProductReviewProps) {
	const { t } = useTranslation(
		PRODUCTS_SLUG_REVIEW_CREATE_I18next.routeProductsSlugReviewCreate,
	)

	const fetcherKey = CREATE_PRODUCT_REVIEW_FORM.fetcherKey
	const fetcher = useFetcher({ key: fetcherKey })

	const formValues = {
		[ROOT_INPUTS.subaction]: CREATE_PRODUCT_REVIEW_FORM.subaction,
		[ROOT_INPUTS.noJs]: 'true',
	}
	const [form, fields] = useForm({
		lastResult: fetcher.data || undefined,
		defaultValue: {
			[CREATE_PRODUCT_REVIEW_INPUTS.product]: product['@id'],
			...(loggedIn && {
				[CREATE_PRODUCT_REVIEW_INPUTS.loggedIn]: 'true',
			}),
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: createProductReviewSchema })
		},
		shouldValidate: 'onBlur',
	})

	return {
		createProductReviewFetcherKey: fetcherKey,
		createProductReviewFetcher: fetcher,
		createProductReviewFormValues: formValues,
		createProductReviewForm: form,
		createProductReviewFields: fields,
		t,
	}
}

export { type UseCreateProductReviewProps, useCreateProductReviewForm }
